/**
 * CommercialLayout — Dark navy layout shell for CRE Pro pages.
 *
 * All /commercial/* routes render inside this layout.
 * Dark theme with fp-navy-900 background, distinct from the residential
 * fp-slate-100 light theme.
 */

import { Suspense } from "react";
import { Outlet, Link, useNavigate } from "react-router";
import { Building2, LogOut } from "lucide-react";
import { useAuthContext } from "../../contexts/AuthContext";
import { ChunkErrorBoundary } from "../../components/ChunkErrorBoundary";

function CommercialNavbar() {
  const { isAuthenticated, signOut, user } = useAuthContext();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate("/commercial");
  };

  return (
    <nav className="bg-fp-navy-900 border-b border-fp-navy-800">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Brand */}
        <Link
          to="/commercial"
          className="flex items-center gap-2.5 group"
        >
          <div className="w-8 h-8 bg-fp-teal-700 rounded-lg flex items-center justify-center group-hover:bg-fp-teal-600 transition-colors">
            <Building2 className="w-4.5 h-4.5 text-white" strokeWidth={2} />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="font-inter font-bold text-lg text-white tracking-tight">
              FairPath
            </span>
            <span className="font-inter font-semibold text-xs text-fp-teal-400 uppercase tracking-widest">
              CRE Pro
            </span>
          </div>
        </Link>

        {/* Right side */}
        <div className="flex items-center gap-4">
          {isAuthenticated ? (
            <>
              <span className="text-sm text-fp-slate-400 hidden sm:block">
                {user?.email}
              </span>
              <button
                onClick={handleSignOut}
                className="flex items-center gap-1.5 text-sm text-fp-slate-400 hover:text-white transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </>
          ) : (
            <Link
              to="/commercial/login"
              className="text-sm font-medium text-fp-teal-400 hover:text-fp-teal-300 transition-colors"
            >
              Sign In
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
}

function CommercialFooter() {
  return (
    <footer className="bg-fp-navy-900 border-t border-fp-navy-800 py-6">
      <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-fp-slate-500 font-inter">
        <span>© 2026 FairPath CRE Pro. All rights reserved.</span>
        <div className="flex items-center gap-4">
          <span>Document preparation service — not legal advice</span>
          <span>·</span>
          <Link
            to="/"
            className="text-fp-slate-400 hover:text-white transition-colors"
          >
            Residential →
          </Link>
        </div>
      </div>
    </footer>
  );
}

export function CommercialLayout() {
  return (
    <div className="min-h-screen bg-fp-navy-900 flex flex-col">
      <CommercialNavbar />
      <main className="flex-1">
        <ChunkErrorBoundary>
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-[60vh]">
                <span className="text-fp-slate-400 text-base">Loading...</span>
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </ChunkErrorBoundary>
      </main>
      <CommercialFooter />
    </div>
  );
}
